import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { clinicPhotos, clinicInfo } from '@/mocks/homeData';

export default function Estrutura() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openLightbox = useCallback((index: number) => {
    setActiveIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % clinicPhotos.length);
  }, []);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + clinicPhotos.length) % clinicPhotos.length);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, closeLightbox, goNext, goPrev]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-100 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-400 via-transparent to-transparent" />
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 text-center">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label"
            >
              Conheça
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl md:text-6xl lg:text-7xl text-foreground-950 mt-4 leading-[1.05]"
            >
              Nossa<br />
              <span className="italic font-light">Estrutura.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-[680px] mx-auto mt-6 text-foreground-600 text-base leading-relaxed"
            >
              Um ambiente projetado para oferecer conforto, privacidade e tecnologia em cada detalhe. Localizado no Duets Office Tower, um dos endereços mais prestigiados de Fortaleza.
            </motion.p>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="relative bg-background-50 py-24 md:py-32 lg:py-40">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {clinicPhotos.map((photo, index) => (
                <motion.div
                  key={photo.src}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: 0.06 * index }}
                  className={`group relative rounded-lg overflow-hidden cursor-pointer ${
                    index === 0 ? 'sm:col-span-2 sm:row-span-2' : ''
                  }`}
                  onClick={() => openLightbox(index)}
                >
                  <div className={index === 0 ? 'aspect-[16/10]' : 'aspect-[4/3]'}>
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <p className="text-white text-sm font-label">{photo.alt}</p>
                    <span className="inline-flex items-center gap-1.5 text-white/80 text-xs mt-1 font-label whitespace-nowrap">
                      <i className="ri-zoom-in-line"></i>
                      Ampliar
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Location Info */}
        <section className="relative bg-background-100 py-20 md:py-28">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">
                  Localização
                </span>
                <h2 className="font-heading text-3xl md:text-5xl text-foreground-950 mt-3 leading-[1.1]">
                  Duets Office Tower<br />
                  <span className="italic font-light">Torre Sul, Sala 610.</span>
                </h2>
                <p className="mt-6 text-sm text-foreground-600 leading-relaxed max-w-lg">
                  No coração do bairro Cocó, um dos endereços mais nobres de Fortaleza. Fácil acesso, estacionamento no local e uma vista privilegiada da cidade.
                </p>
                <div className="mt-6 space-y-3 text-sm text-foreground-600">
                  <p className="flex items-start gap-3">
                    <i className="ri-map-pin-line text-primary-500 mt-0.5"></i>
                    <span>
                      {clinicInfo.address.street}<br />
                      {clinicInfo.address.complement}<br />
                      {clinicInfo.address.neighborhood} - {clinicInfo.address.city}/{clinicInfo.address.state}
                    </span>
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="ri-phone-line text-primary-500"></i>
                    {clinicInfo.phone}
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="ri-time-line text-primary-500"></i>
                    {clinicInfo.hours}
                  </p>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
                className="rounded-lg overflow-hidden aspect-[4/3]"
              >
                <iframe
                  src={clinicInfo.googleMapsEmbed}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localização Clínica Femme Vie"
                  className="w-full h-full"
                ></iframe>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Lightbox */}
        <AnimatePresence>
          {lightboxOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            >
              <button
                onClick={closeLightbox}
                className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
                aria-label="Fechar"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <button
                onClick={goPrev}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
                aria-label="Anterior"
              >
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
              <button
                onClick={goNext}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
                aria-label="Próxima"
              >
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
              <div className="relative max-w-[90vw] max-h-[85vh]">
                <motion.img
                  key={activeIndex}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  src={clinicPhotos[activeIndex].src}
                  alt={clinicPhotos[activeIndex].alt}
                  className="max-w-full max-h-[85vh] object-contain rounded-sm"
                />
                <p className="absolute -bottom-10 left-0 right-0 text-center text-white/60 text-sm font-label">
                  {clinicPhotos[activeIndex].alt} ({activeIndex + 1} / {clinicPhotos.length})
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
}